<script>
    import { onMount } from "svelte";
    import { push } from "svelte-spa-router";
    import Flashcard from "@edu-quizz/shared/components/flashcard/Flashcard.svelte";
    import FlashcardIntensiveCompletion from "@edu-quizz/shared/components/flashcard/FlashcardIntensiveCompletion.svelte";
    import ProgressBar from "@edu-quizz/shared/components/common/ProgressBar.svelte";
    import NavigationButtons from "@edu-quizz/shared/components/common/NavigationButtons.svelte";
    import DifficultyButtons from "@edu-quizz/shared/components/common/DifficultyButtons.svelte";
    import BackButton from "@edu-quizz/shared/components/common/BackButton.svelte";
    import { loadDeckIntensive, saveReviewIntensive, recycleNonCompletedCards, resetDeckReviews } from "../lib/flashcardLogic.js";

    export let params = {};

    let deck = null;
    let cards = [];
    let loading = true;
    let error = null;

    // Flashcard state
    let currentCardIndex = 0;
    let isFlipped = false;
    let showResults = false;
    let reviewedCards = [];
    let completedCardIds = new Set(); // Track cards marked as easy

    $: currentCard = cards[currentCardIndex];
    $: console.log("[currentCard]", currentCard);
    $: currentCardIsReversed = currentCard?.isReversed || false;
    $: hasNext = currentCardIndex < cards.length - 1;
    $: hasPrev = currentCardIndex > 0;
    $: completionRate = cards.length > 0 ? Math.round((completedCardIds.size / cards.length) * 100) : 0;

    async function loadDeck() {
        loading = true;
        error = null;

        try {
            const deckId = parseInt(params.id);
            const result = await loadDeckIntensive(deckId);
            console.log("[result]", result);

            deck = result.deck;
            cards = result.cards;
            reviewedCards = new Array(cards.length).fill(null);
            completedCardIds = result.fullyLearnedCardIds || new Set();

            // If all cards are already mastered, show completion screen immediately
            if (cards.length > 0 && completedCardIds.size === cards.length) {
                showResults = true;
            }

            loading = false;
        } catch (err) {
            console.error("Error loading deck:", err);
            error = err.message;
            loading = false;
        }
    }

    function flipCard() {
        isFlipped = !isFlipped;
    }

    function nextCard() {
        if (hasNext) {
            currentCardIndex++;
            isFlipped = false;
        } else if (
            currentCardIndex === cards.length - 1 &&
            reviewedCards.every((r) => r !== null)
        ) {
            // Check if we should recycle cards
            if (completedCardIds.size < cards.length) {
                // Recycle non-completed cards
                handleRecycleCards();
            } else {
                // All cards completed, show results
                showResults = true;
            }
        }
    }

    function handleRecycleCards() {
        // Get original cards from deck (before filtering)
        const originalCards = deck.cards || [];

        // Get non-completed cards
        const recycledCards = recycleNonCompletedCards(originalCards, completedCardIds);

        if (recycledCards.length > 0) {
            // Reset to study non-completed cards again
            cards = recycledCards;
            currentCardIndex = 0;
            isFlipped = false;
            reviewedCards = new Array(cards.length).fill(null);
        } else {
            // All cards completed
            showResults = true;
        }
    }

    function prevCard() {
        if (hasPrev) {
            currentCardIndex--;
            isFlipped = false;
        }
    }

    async function markDifficulty(level) {
        // Prevent marking the same card multiple times
        if (reviewedCards[currentCardIndex] !== null) {
            return;
        }

        reviewedCards[currentCardIndex] = level;

        // Mark cards as completed if they're marked as easy
        if (level === 1) {
            completedCardIds.add(currentCard.serverId);
            completedCardIds = completedCardIds; // Trigger reactivity
        }

        // Save the review to the database
        try {
            await saveReviewIntensive(deck, currentCard, level);
        } catch (err) {
            console.error("Error saving flashcard review:", err);
        }

        nextCard();
    }

    function restart() {
        currentCardIndex = 0;
        isFlipped = false;
        showResults = false;
        completedCardIds = new Set();
        loadDeck();
    }

    async function handleResetDeck() {
        if (
            !confirm(
                "Voulez-vous vraiment réinitialiser toutes les révisions de ce deck ? Vous pourrez réviser toutes les cartes à nouveau.",
            )
        ) {
            return;
        }

        try {
            const deckId = parseInt(params.id);
            await resetDeckReviews(deckId);

            // Return to mode selection
            push(`/flashcard/${deckId}`);
        } catch (err) {
            console.error("Error resetting deck reviews:", err);
            error = "Erreur lors de la réinitialisation des révisions";
        }
    }

    function goBack() {
        push("/");
    }

    function handleKeyPress(event) {
        if (showResults) return;

        if (event.key === " " || event.key === "Enter") {
            event.preventDefault();
            if (!isFlipped) {
                flipCard();
            }
        } else if (event.key === "ArrowRight") {
            event.preventDefault();
            if (isFlipped) {
                nextCard();
            }
        } else if (event.key === "ArrowLeft") {
            event.preventDefault();
            prevCard();
        } else if (isFlipped && event.key >= "1" && event.key <= "3") {
            event.preventDefault();
            markDifficulty(parseInt(event.key));
        }
    }

    onMount(() => {
        loadDeck();
        window.addEventListener("keydown", handleKeyPress);
        return () => {
            window.removeEventListener("keydown", handleKeyPress);
        };
    });
</script>

<div class="flashcard-page">
    <header class="page-header">
        <BackButton onClick={goBack} />
        {#if deck}
            <div class="header-content">
                <h1 class="text-base-content">{deck.name}</h1>
                <span class="mode-badge bg-secondary text-secondary-content">Mode intensif</span>
            </div>
        {/if}
    </header>

    {#if loading}
        <div class="loading">
            <div class="spinner loading-spinner loading-lg border-primary border-t-transparent"></div>
            <p>Chargement du deck...</p>
        </div>
    {:else if error}
        <div class="error text-base-content/70">
            <p>Erreur: {error}</p>
            <button class="btn btn-primary" on:click={loadDeck}>Réessayer</button>
        </div>
    {:else if showResults}
        <FlashcardIntensiveCompletion
            totalCards={deck.cards?.length || 0}
            onBack={goBack}
            onReset={handleResetDeck}
        />
    {:else}
        <div class="deck-container">
            {#if deck.description}
                <p class="deck-description bg-base-200">{deck.description}</p>
            {/if}

            <!-- Completion indicator for intensive mode -->
            <div class="completion-indicator">
                <div class="completion-text text-base-content/70">
                    <span>Cartes maîtrisées: {completedCardIds.size} / {deck.cards?.length || 0}</span>
                    <span class="completion-percentage">{completionRate}%</span>
                </div>
                <div class="completion-bar bg-base-300">
                    <div class="completion-fill bg-success" style="width: {completionRate}%"></div>
                </div>
            </div>

            <ProgressBar
                current={currentCardIndex + 1}
                total={cards.length}
                label="Carte {currentCardIndex + 1} sur {cards.length}"
            />

            <Flashcard
                {isFlipped}
                frontContent={currentCardIsReversed ? currentCard.back : currentCard.front}
                backContent={currentCardIsReversed ? currentCard.front : currentCard.back}
                frontImages={currentCardIsReversed ? currentCard.answerImages : currentCard.questionImages}
                backImages={currentCardIsReversed ? currentCard.questionImages : currentCard.answerImages}
                frontAudio={currentCardIsReversed ? null : currentCard.questionAudio}
                onFlip={flipCard}
            />

            {#if isFlipped}
                <DifficultyButtons
                    onMarkDifficulty={markDifficulty}
                    isReviewed={reviewedCards[currentCardIndex] !== null}
                />
            {:else}
                <NavigationButtons
                    onPrev={prevCard}
                    onNext={nextCard}
                    {hasPrev}
                    {hasNext}
                />
            {/if}

            <div class="keyboard-hints text-base-content/50">
                <span><kbd class="kbd kbd-sm bg-base-200 border-base-300">Espace</kbd> Retourner la carte</span>
                <span><kbd class="kbd kbd-sm bg-base-200 border-base-300">←</kbd> Précédent</span>
                <span><kbd class="kbd kbd-sm bg-base-200 border-base-300">→</kbd> Suivant</span>
                {#if isFlipped}
                    <span><kbd class="kbd kbd-sm bg-base-200 border-base-300">1-3</kbd> Difficulté</span>
                {/if}
            </div>

            <div class="info-box bg-info/10 text-info border border-info/20">
                <p>
                    <strong>Mode intensif:</strong> Les cartes marquées comme "difficiles" ou "moyennes" seront repassées jusqu'à ce que vous les maîtrisiez (marqué comme "facile").
                </p>
            </div>
        </div>
    {/if}
</div>

<style>
    .flashcard-page {
        min-height: 100vh;
        padding: 1.5rem;
        max-width: 800px;
        margin: 0 auto;
    }

    .page-header {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 2rem;
    }

    .header-content {
        display: flex;
        align-items: center;
        gap: 1rem;
        flex-wrap: wrap;
    }

    h1 {
        margin: 0;
        font-size: 2rem;
    }

    .mode-badge {
        padding: 0.25rem 0.75rem;
        border-radius: 9999px;
        font-size: 0.875rem;
        font-weight: 600;
    }

    .loading,
    .error {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 60vh;
        gap: 1rem;
    }

    .spinner {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }

    .error button {
        border: none;
        padding: 0.6rem 1.2rem;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.95rem;
    }

    .deck-container {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .deck-description {
        font-size: 1rem;
        line-height: 1.6;
        margin: 0;
        padding: 1rem;
        border-radius: 8px;
    }

    .completion-indicator {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .completion-text {
        display: flex;
        justify-content: space-between;
        font-size: 0.875rem;
        font-weight: 500;
    }

    .completion-percentage {
        font-weight: 700;
    }

    .completion-bar {
        height: 8px;
        border-radius: 9999px;
        overflow: hidden;
    }

    .completion-fill {
        height: 100%;
        transition: width 0.3s ease;
        border-radius: 9999px;
    }

    .keyboard-hints {
        display: flex;
        gap: 1rem;
        justify-content: center;
        flex-wrap: wrap;
        font-size: 0.85rem;
    }

    kbd {
        padding: 0.2rem 0.5rem;
        border-radius: 4px;
        border: 1px solid;
        font-family: monospace;
        font-size: 0.8rem;
    }

    .info-box {
        padding: 1rem;
        border-radius: 8px;
        font-size: 0.875rem;
        line-height: 1.5;
    }

    .info-box p {
        margin: 0;
    }

    @media (max-width: 640px) {
        .flashcard-page {
            padding: 1rem;
        }

        h1 {
            font-size: 1.5rem;
        }

        .keyboard-hints {
            display: none;
        }
    }
</style>
